/**
 * How a domain reads on HQ's Company and Domain tab.
 *
 * **No function here returns a verification token, and none should.** The customer reads the TXT
 * value from their own portal and publishes it; HQ shows where a domain has got to and never the
 * credential itself, which is the user's decision of 2026-09-10. The API's add-domain answer does
 * not carry the token either, so there is nothing here to leak.
 */
import { AllowancePool } from './hooks/customer-account-allowances';
import { poolState } from './allowance-format';

/** What the `websites.status` column can hold, including the rows that predate it being written. */
export type DomainStatus = 'verified' | 'unverified' | 'unset';

export function domainStatus(status: string|undefined|null): DomainStatus {
    if (status === 'verified') {
        return 'verified';
    }
    if (status === 'unverified') {
        return 'unverified';
    }

    // Not a theoretical case: five of the eight rows in the local database carry an empty
    // string, from before the portal wrote a status at all. Reading those as unverified would
    // claim the customer has been asked to do something they have not been.
    return 'unset';
}

export function domainStatusLabel(status: string|undefined|null): string {
    switch (domainStatus(status)) {
        case 'verified':
            return 'Verified';
        case 'unverified':
            return 'Unverified';
        default:
            return 'No status';
    }
}

/** The token a status chip is painted in. Verified is the only good news, so it is the only colour. */
export function domainStatusInk(status: string|undefined|null): string {
    switch (domainStatus(status)) {
        case 'verified':
            return 'var(--wf-ok, #15803d)';
        case 'unverified':
            return 'var(--wf-live-line, #b08c00)';
        default:
            return 'var(--wf-ink-faint, #74909b)';
    }
}

/**
 * What to say about the token beside an unverified domain, or an empty string when there is
 * nothing worth saying.
 *
 * A verified domain gets no note whatever its token says: the DNS record has already been seen,
 * and an expiry that has passed since is not something anybody needs to act on.
 */
export function domainTokenNote(
    status: string|undefined|null,
    tokenExpires: number|undefined|null,
    now: number = Date.now()
): string {
    if (domainStatus(status) === 'verified') {
        return '';
    }
    if (tokenExpires === undefined || tokenExpires === null) {
        return 'No token yet. The customer generates one on their Organisations screen.';
    }

    const secondsLeft: number = tokenExpires - Math.floor(now / 1000);
    if (secondsLeft <= 0) {
        return 'Token expired. The customer generates a new one on their Organisations screen.';
    }
    const hoursLeft: number = Math.floor(secondsLeft / 3600);
    if (hoursLeft < 1) {
        return 'Token expires in under an hour.';
    }

    return 'Token expires in ' + hoursLeft + (hoursLeft === 1 ? ' hour.' : ' hours.');
}

/**
 * Why the admin cannot add a domain to this company, or undefined when they can.
 *
 * The API refuses each of these on its own; this is so the admin is told before they type, in the
 * same words the refusal would use. The same shape as `addSeatBlockedReason` on the Seats tab.
 */
export function addDomainBlockedReason(params: {
    loading: boolean,
    pool: AllowancePool|undefined,
    hasCustomerAccount: boolean,
}): string|undefined {
    if (!params.hasCustomerAccount) {
        return 'This company has no customer account, so there is no subscription to spend a domain from.';
    }
    if (params.loading) {
        return 'Checking what this account\'s plan allows.';
    }

    // `poolState` rather than `headroom` alone, so an account **over** its allowance is not
    // described as merely full. Those exist: nothing enforced these limits on the admin side
    // until 2026-09-10, and account 1 in the local database is at two domains against one.
    switch (poolState(params.pool)) {
        case 'over':
            return 'This account is over its domain allowance, at ' + (params.pool?.used ?? 0)
                + ' against ' + (params.pool?.allowed ?? 0)
                + '. Add a domain bolt-on on the Subscription tab first.';
        case 'full':
            return 'All ' + (params.pool?.allowed ?? 0)
                + ' of this plan\'s domains are in use. Add a domain bolt-on on the Subscription tab first.';
        default:
            return undefined;
    }
}

/**
 * Whether what the admin has typed is a domain we will store, mirroring `DomainName` in HQ-API.
 *
 * Deliberately looser than the server: it exists to stop the obvious mistakes without disagreeing
 * with the refusal that follows, so anything it lets through is judged by the API and reported in
 * the API's own words.
 */
export function looksLikeADomain(input: string): boolean {
    const trimmed: string = input.trim().toLowerCase();
    if (trimmed === '') {
        return false;
    }
    const withScheme: string = trimmed.includes('://') ? trimmed : 'https://' + trimmed;
    let url: URL;
    try {
        url = new URL(withScheme);
    } catch {
        return false;
    }
    if (url.protocol !== 'https:' || url.port !== '' || url.username !== '' || url.search !== '' || url.hash !== '') {
        return false;
    }
    if (url.pathname !== '/' && url.pathname !== '') {
        return false;
    }

    return /^(?:www\.)?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/.test(url.hostname);
}
